/**
 * @copyright (C) 2025, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Charyung Kim (crkim@rsupport.com)
 *
 * @description
 *
 * @created at Fri Jan 10 2025
 **/

import { EventHandler } from "@rfice/eventhandler";
import { createImage } from "@rstudio/util-v2";

import { AttrProps, getCurrentImageSize, getSourceResolution, makeExCoordinate } from "./utils";
import { OBJECT_TYPE, PEN_TYPE } from "./types/enum";

const EMPTY_OBJECT: IWhiteboardDrawingObject = {
	penType: PEN_TYPE.PEN,
	strokeColor: "#000",
	fillColor: "transparent",
	thickness: 0,
	alpha: 1,
	objectType: OBJECT_TYPE.EMPTY,
	points: [makeExCoordinate({ x: 0, y: 0 })],
	emojiSourceIndex: 0
};

// 캔버스 엘리먼트를 사용한 그리기 객체
export class DrawingManager implements IWhiteboardProps {
	backgroundColor: string = "transparent";
	background?: CanvasImageSource;
	resolution: Resolution = { width: 1920, height: 1080 };
	historyMax: number = 11;
	useCacheImage = true;

	private objectList: ObjectList[] = [[]];
	private pageIndex: number = 0;
	private currentIndex: number = -1;
	private canvas = document.createElement("canvas");
	private imageCanvas = document.createElement("canvas");
	private ctx?: CanvasRenderingContext2D;
	private pendingList: { object: IWhiteboardDrawingObject; attrProps: AttrProps | undefined }[] = [];
	private isCreatingImage = false;

	// creates an instance of whiteBoard
	constructor(props?: IWhiteboardProps) {
		this.setProps(props);
		this.initialize();
		this.clearHistory();
	}

	// 프로퍼티 설정 변경
	public async setProps(props?: IWhiteboardProps) {
		if (
			props?.resolution &&
			(props.resolution.width !== this.resolution.width || props.resolution.height !== this.resolution.height)
		) {
			const xRatio = props.resolution.width / this.resolution.width;
			const yRatio = props.resolution.height / this.resolution.height;
			for (const obj of this.objectList[this.pageIndex]) {
				obj.points?.forEach((point) => {
					point.x *= xRatio;
					point.y *= yRatio;
				});
			}
		}
		if (props) Object.assign(this, props);
	}

	// 캔버스 초기화
	private initialize() {
		this.setupCanvas();
		this.clearAll();
	}

	// 캔버스 사이즈 설정 및 컨텍스트 초기화
	private setupCanvas() {
		this.canvas.width = this.resolution.width;
		this.canvas.height = this.resolution.height;
		this.ctx = this.canvas.getContext("2d") as CanvasRenderingContext2D;
	}

	// Resolution 변경
	public setResolution(resolution: Resolution) {
		this.resolution.width = resolution.width;
		this.resolution.height = resolution.height;
		this.canvas.width = this.resolution.width;
		this.canvas.height = this.resolution.height;
	}

	// Presentation Page init
	public initPageCount(pageCount: number) {
		for (let i = 0; i < pageCount - 1; i++) {
			this.objectList.push([EMPTY_OBJECT]);
		}
	}

	// Presentation Page 변경
	public updatePageNumber(pageIndex: number) {
		this.pageIndex = pageIndex;
		this.updateCurrentIndex(this.objectList[pageIndex].length - 1);
		this.ctx?.clearRect(0, 0, this.canvas.width, this.canvas.height);
	}

	// current index 업데이트
	private updateCurrentIndex(targetIndex: number) {
		// update index
		this.currentIndex = targetIndex;
		// fire event
		EventHandler.fire("$DrawingManager::UndoRedoStateUpdated$", {
			undo: this.currentIndex >= 1,
			redo: this.currentIndex < this.objectList[this.pageIndex].length - 1
		});
	}

	// 이미지 layer 그리기
	public draw() {
		// check pending list
		if (!this.isCreatingImage && this.pendingList.length > 0) {
			this.addOnList(this.pendingList[0].object, this.pendingList[0].attrProps);
			this.pendingList.shift();
		}

		const ctx = this.ctx;
		if (ctx) {
			// clear rect
			ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

			// draw object image
			const objectList = this.objectList[this.pageIndex];
			const object = objectList[this.currentIndex];
			if (object && object.image && object.position) {
				ctx.drawImage(object.image, object.position.left, object.position.top);
			} else {
				const prevObject = objectList[this.currentIndex - 1];
				if (prevObject && prevObject.image && prevObject.position) {
					ctx.drawImage(prevObject.image, prevObject.position.left, prevObject.position.top);
				}
			}
		}
	}

	// controller에서 그려진 object를 list에 추가
	public async addOnList(object: IWhiteboardDrawingObject, attrProps?: AttrProps) {
		// 이미지가 생성중이라면 pendingList에 추가한다
		if (this.isCreatingImage) {
			this.pendingList.push({ object, attrProps });
			return;
		}
		const objectList = this.objectList[this.pageIndex];
		if (this.historyMax < this.currentIndex) {
			objectList.splice(0, this.currentIndex - this.historyMax + 1);
			this.updateCurrentIndex(objectList.length - 1);
		} else if (objectList.length - 1 !== this.currentIndex) {
			objectList.splice(this.currentIndex + 1, objectList.length);
		}
		objectList.push(object);
		this.updateCurrentIndex(this.currentIndex + 1);
		if (this.useCacheImage) {
			this.isCreatingImage = true;
			await this.makeCurrentImage(attrProps);
			this.isCreatingImage = false;
		}
	}

	// 현재까지 그려진 내용을 이미지로 만들어서 그리기 오브젝트에 세팅
	private async makeCurrentImage(attrProps?: AttrProps) {
		const sizeProps = {
			arrowSize: attrProps?.arrowSize || 15,
			emojiSize: attrProps?.emojiSize || { width: 50, height: 50 },
			imageSrc: attrProps?.imageSrc,
			screenSize: this.resolution,
			imgSrcSize: getSourceResolution(attrProps?.imageSrc)
		};
		const objectList = this.objectList[this.pageIndex];
		const object = objectList[this.currentIndex];
		const prevObject = objectList[this.currentIndex - 1];
		const position = getCurrentImageSize({ object, prevObject }, sizeProps);

		if (position) {
			object.position = position;
			this.imageCanvas.width = position.right - position.left;
			this.imageCanvas.height = position.bottom - position.top;
			const ctx = this.imageCanvas.getContext("2d");
			if (ctx) {
				ctx.imageSmoothingEnabled = false;
				ctx.clearRect(0, 0, this.resolution.width, this.resolution.height);
				ctx.translate(-position.left, -position.top);
				if (object?.objectType !== OBJECT_TYPE.EMPTY) {
					// prev object의 image layer
					if (prevObject?.image && prevObject.position) {
						ctx.drawImage(prevObject.image, prevObject.position.left, prevObject.position.top);
					}
					// current object의 drawing layer
					object?.redrawObject?.(ctx);
				}
				ctx.translate(position.left, position.top);
				const dataUrl = this.imageCanvas.toDataURL("image/png").replace("image/png", "image/octet-stream");
				try {
					object.image = await createImage(dataUrl);
				} catch (error) {
					console.log("Error occured in makeCurrentImage", error);
				} finally {
					this.isCreatingImage = false;
				}
			}
		}
	}

	// 히스토리 지우기
	public clearHistory() {
		this.objectList[this.pageIndex] = [
			{
				penType: PEN_TYPE.ERASER,
				strokeColor: "transparent",
				fillColor: "transparent",
				alpha: 0,
				thickness: 0,
				emojiSourceIndex: 0
			}
		];
		this.updateCurrentIndex(0);
	}

	// 현재보다 한단계 전 그리기 상태로 변경
	public undo() {
		if (this.currentIndex >= 1) this.updateCurrentIndex(this.currentIndex - 1);
	}

	// undo 이전 상태로 변경
	public redo() {
		if (this.currentIndex < this.objectList[this.pageIndex].length - 1)
			this.updateCurrentIndex(this.currentIndex + 1);
	}

	// 화면 전체 지우기
	public clearAll() {
		this.addOnList(EMPTY_OBJECT);
	}

	// 화이트보드를 그리는 캔버스의 context를 반환
	public getOutputCtx(): CanvasRenderingContext2D | undefined {
		return this.ctx;
	}

	// 화이트보드 resolution 반환
	public getResolution(): Resolution {
		return this.resolution;
	}
}
